import React, { useState, useMemo, useEffect, useCallback } from 'react';
import '../../assets/css/StudentDashboard.css';
import { useHasPermission } from '../../Utils/checkPermissions';
import AdvanceTable from '../../Utils/AdvanceTables';
import Searching from '../../Utils/Searching';
import { getData, patchData } from '../../API/GlobalApi';
import { formatDateDDMMYYYYHHMMSS_UTC } from '../../Utils/data&dateTime';
import { copyToClipboard } from '../../Utils/copyToClipboard';
import { AddDefaulterModal } from '../../Component/Modals/AddDefaulterModal';
import Notifier from '../../Utils/notify';
import TableSkeleton from '../../Utils/TableSkeleton';

const ManageDefaulters = () => {
    const hasPermission = useHasPermission();
    const [defaulters, setDefaulters] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [, setIsSearching] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [filterQueryString, setFilterQueryString] = useState('');
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [removeModal, setRemoveModal] = useState({ open: false, id: null });
    const [removeRemark, setRemoveRemark] = useState('');
    const [removeLoading, setRemoveLoading] = useState(false);

    const pageSize = 10;
    const pageCounts = Math.ceil(totalCount / pageSize);

    const fetchDefaulters = useCallback(async (page = 0) => {
        setLoading(true);
        try {
            const response = await getData(`students/defaulter-list/?page=${page + 1}`);
            if (Array.isArray(response)) {
                setDefaulters(response);
                setTotalCount(response.length);
            } else if (response?.results) {
                setDefaulters(response.results);
                setTotalCount(response.count || response.results.length);
            } else {
                setDefaulters([]);
                setTotalCount(0);
            }
        } catch {
            Notifier.error('Failed to fetch defaulters.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isSearchMode) {
            fetchDefaulters(currentPage);
        }
    }, [currentPage, isSearchMode, fetchDefaulters]);

    const handlePageChange = ({ selected }) => {
        setCurrentPage(selected);
    };

    const openRemoveModal = useCallback((id, student_id) => {
        setRemoveModal({ open: true, id, student_id });
        setRemoveRemark('');
    }, []);

 
    const handleRemoveDefaulter = async () => {
        if (!removeRemark.trim()) {
            Notifier.warn('Please enter a remark for removal.');
            return;
        }
        setRemoveLoading(true);
        try {
            const response = await patchData(`students/defaulter-list/`, {
                student_id: removeModal.student_id,
                remark: removeRemark.trim(),
            });
            if (response?.error) {
                Notifier.error(response.error);
            } else {
                Notifier.success('Student removed from defaulters.');
                setRemoveModal({ open: false, id: null });
                setRemoveRemark('');
                fetchDefaulters(currentPage);
            }
        } catch {
            Notifier.error('Failed to remove defaulter.');
        } finally {
            setRemoveLoading(false);
        }
    };

    const haspermissions = useHasPermission();

    const columns = useMemo(() => {
        const baseColumns = [
            {
                Header: 'Student Name',
                accessor: 'student_name',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div>
                        <p style={{ textAlign: 'left', color: '#222F3E', fontWeight: 500, margin: 0 }}>
                            {row.original.student_name || '—'}
                        </p>
                        <p
                            style={{
                                textAlign: 'left',
                                color: '#7F56DA',
                                fontSize: '12px',
                                margin: 0,
                                textDecoration: 'underline',
                                cursor: 'pointer'
                            }}
                            title="Copy Reg No"
                            onClick={() => copyToClipboard(row.original.registration_number, 'Reg No copied')}
                        >
                            {row.original.registration_number || '—'}
                        </p>
                    </div>
                ),
            },
            {
                Header: 'Contact No',
                accessor: 'mobile',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div className="no-text-cursor">
                        <p style={{ textAlign: 'left', color: '#222F3E', margin: 0 }}>
                            {row.original.mobile || '—'}
                        </p>
                    </div>
                ),
            },
            {
                Header: 'Course',
                accessor: 'course',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div className="no-text-cursor">
                        <p style={{ textAlign: 'left', color: '#222F3E', margin: 0 }}>
                            {row.original.course || '—'}
                        </p>
                    </div>
                ),
            },
            {
                Header: 'Remark',
                accessor: 'remark',
                disableSortBy: true,
                Cell: ({ row }) => {
                    const remark = row.original.remark || '—';
                    return (
                        <div className="no-text-cursor" title={remark}>
                            <p
                                style={{
                                    textAlign: 'left',
                                    color: '#222F3E',
                                    margin: 0,
                                    maxWidth: '220px',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {remark}
                            </p>
                        </div>
                    );
                },
            },
            {
                Header: 'Added By',
                accessor: 'added_by',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div className="no-text-cursor">
                        <p style={{ textAlign: 'left', color: '#222F3E', fontWeight: 500, margin: 0 }}>
                            {row.original.added_by || '—'}
                        </p>
                        <p style={{ textAlign: 'left', color: '#888', fontSize: '12px', margin: 0 }}>
                            {row.original.added_at
                                ? formatDateDDMMYYYYHHMMSS_UTC(row.original.added_at.replace(' ', 'T'))
                                : ''}
                        </p>
                    </div>
                ),
            },
        ];

        if (haspermissions('can-remove-defaulters')) {
            baseColumns.push({
                Header: 'Action',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            title="Remove Defaulter"
                            onClick={() => openRemoveModal(row.original.id, row.original.student_id)}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px',
                                color: '#D92D20',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <line x1="23" y1="11" x2="17" y2="11" />
                            </svg>
                        </button>
                    </div>
                ),
            });
        }

        return baseColumns;
    }, [openRemoveModal, haspermissions]);

    const formattedData = useMemo(() =>
        defaulters.map((item) => ({
            id: item.id,
            student_id: item.id,
            registration_number: item.registration_number || '',
            student_name: item.name || '',
            mobile: item.mobile || '',
            course: item.course || '',
            remark: item.defaulter_add_remark || '',
            added_by: item.defaulter_updated_by || '',
            added_at: item.defaulter_updated_at || '',
        })),
        [defaulters]
    );

    const handleSearchClear = () => {
        setIsSearchMode(false);
        setSearchValue('');
        setFilterQueryString('');
        setCurrentPage(0);
        fetchDefaulters(0);
    };

    if (!hasPermission("can-view-defaulter-list")) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#888' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D92D20" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px' }}>
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h5 style={{ color: '#222F3E', fontWeight: 600 }}>Access Denied</h5>
                <p style={{ fontSize: '14px' }}>You do not have permission to view this page.</p>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', position: 'relative' }}>

            {/* Title row */}
            <div style={{ flexShrink: 0, padding: '0 12px' }}>
                <h2 className="main-heading">Manage Defaulters ({totalCount})</h2>
            </div>

            {/* Search + Add button row */}
            <div style={{ flexShrink: 0, padding: '8px 12px' }}>
                <div className="row" style={{ alignItems: 'center' }}>
                    <div className="col-md-6">
                        <Searching
                            placeholder="Search by Name, Reg. No, Phone No..."
                            filterQueryString={filterQueryString}
                            url="students/defaulter-list/"
                            setResults={setDefaulters}
                            setTotalCount={setTotalCount}
                            setFilterQueryString={setFilterQueryString}
                            setSearchLoading={setSearchLoading}
                            setCurrentPage={setCurrentPage}
                            setIsSearching={setIsSearching}
                            setIsSearchMode={setIsSearchMode}
                            setSearchValue={setSearchValue}
                            onSearchClear={handleSearchClear}
                            searchValue={searchValue}
                        />
                    </div>
                    <div className="col-md-6 d-flex justify-content-end">
                        <button className="add-btn" onClick={() => setAddModalOpen(true)}>
                            <span className="me-2">
                                <svg width="12" height="12" viewBox="0 0 22 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M20.4286 13.3537H12.5714V21.2108C12.5714 21.6276 12.4059 22.0273 12.1112 22.322C11.8165 22.6167 11.4168 22.7822 11 22.7822C10.5832 22.7822 10.1835 22.6167 9.88883 22.322C9.59413 22.0273 9.42857 21.6276 9.42857 21.2108V13.3537H1.57143C1.15466 13.3537 0.754961 13.1881 0.460261 12.8934C0.165561 12.5987 0 12.199 0 11.7822C0 11.3655 0.165561 10.9658 0.460261 10.6711C0.754961 10.3764 1.15466 10.2108 1.57143 10.2108H9.42857V2.35366C9.42857 1.93689 9.59413 1.53719 9.88883 1.24249C10.1835 0.947787 10.5832 0.782227 11 0.782227C11.4168 0.782227 11.8165 0.947787 12.1112 1.24249C12.4059 1.53719 12.5714 1.93689 12.5714 2.35366V10.2108H20.4286C20.8453 10.2108 21.245 10.3764 21.5397 10.6711C21.8344 10.9658 22 11.3655 22 11.7822C22 12.199 21.8344 12.5987 21.5397 12.8934C21.245 13.1881 20.8453 13.3537 20.4286 13.3537Z" fill="white" />
                                </svg>
                            </span>
                            Add Defaulter
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 12px' }}>
                {loading || searchLoading ? (
                    <TableSkeleton />
                ) : (
                    <AdvanceTable
                        data={formattedData}
                        columns={columns}
                        currentPage={currentPage}
                        pageCounts={pageCounts}
                        handlePageChange={handlePageChange}
                        searchValue={searchValue}
                        showPagination={true}
                    />
                )}
            </div>

            {/* Remove Defaulter Remark Modal */}
            {removeModal.open && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ position: 'relative', width: '40%' }}>
                        <div className="modal-header-content d-flex justify-content-between align-items-center mb-3">
                            <h4 className="text-primary">Remove Defaulter</h4>
                            <button className="close-btn" onClick={() => setRemoveModal({ open: false, id: null })}>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M1 1L13 13M13 1L1 13" stroke="#333" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body" style={{ overflowY: 'unset' }}>
                            <label style={{ fontWeight: 600, fontSize: '14px', marginBottom: '6px', display: 'block' }}>
                                Remark <span style={{ color: 'red' }}>*</span>
                            </label>
                            <textarea
                                rows={3}
                                value={removeRemark}
                                onChange={e => setRemoveRemark(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleRemoveDefaulter();
                                    }
                                }}
                                placeholder="Reason for removing defaulter..."
                                style={{
                                    width: '100%', border: '1px solid #ddd', borderRadius: '8px',
                                    padding: '10px', fontSize: '14px', resize: 'vertical', outline: 'none',
                                }}
                            />
                            <div className="d-flex justify-content-end gap-2 mt-4">
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setRemoveModal({ open: false, id: null })}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="add-btn"
                                    onClick={handleRemoveDefaulter}
                                    disabled={removeLoading}
                                >
                                    {removeLoading
                                        ? <span className="spinner-border spinner-border-sm" role="status" />
                                        : 'Confirm Remove'
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Defaulter Modal */}
            <AddDefaulterModal
                isOpen={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                onSuccess={() => {
                    setCurrentPage(0);
                    fetchDefaulters(0);
                }}
            />
        </div>
    );
};

export default ManageDefaulters;
